import React, { useState } from 'react';
import { createCheckoutSession, redirectToCheckout } from '../services/stripeApi';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';

/**
 * Stripe Checkout Button Component
 * Handles creation of checkout session and redirect to Stripe's hosted checkout page
 */
const StripeCheckoutButton = ({
    orderData,
    onSuccess,
    onError,
    onCancel,
    disabled = false,
    children
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCheckout = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Get order data - it might be a function that creates the order
            let finalOrderData = orderData;
            if (typeof orderData === 'function') {
                finalOrderData = await orderData();
                if (!finalOrderData) {
                    throw new Error('Failed to create order');
                }
            }

            // Create checkout session
            const response = await createCheckoutSession(finalOrderData);

            if (response.sessionId) {
                // Store order ID in localStorage for abandonment tracking
                if (finalOrderData.orderId) {
                    localStorage.setItem('pendingOrderId', finalOrderData.orderId);
                    console.log('💾 Stored pending order ID for abandonment tracking:', finalOrderData.orderId);
                }

                // Redirect to Stripe Checkout
                await redirectToCheckout(response.sessionId);
            } else {
                throw new Error('No session ID received');
            }

        } catch (error) {
            console.error('❌ Stripe Checkout error:', error);
            setError(error.message || 'Failed to start checkout process');
            onError?.(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="stripe-checkout-container">
            {error && (
                <div className="error-message" style={{
                    color: '#ff7b72',
                    backgroundColor: 'rgba(255, 69, 58, 0.1)',
                    border: '1px solid rgba(255, 69, 58, 0.25)',
                    borderRadius: '16px',
                    padding: '16px 20px',
                    marginBottom: '20px',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span>⚠️</span> {error}
                </div>
            )}

            <Button
                onClick={handleCheckout}
                disabled={disabled || isLoading}
                variant="primary"
                style={{
                    background: 'linear-gradient(135deg, #0a84ff 0%, #0056b3 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    padding: '18px 32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    width: '100%',
                    color: 'white',
                    boxShadow: '0 20px 40px -10px rgba(10, 132, 255, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
                    opacity: disabled || isLoading ? 0.7 : 1,
                    transform: disabled || isLoading ? 'none' : undefined
                }}
                onMouseEnter={(e) => {
                    if (!disabled && !isLoading) {
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
                        e.currentTarget.style.boxShadow = '0 30px 60px -15px rgba(10, 132, 255, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!disabled && !isLoading) {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(10, 132, 255, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                    }
                }}
            >
                {isLoading ? (
                    <>
                        <LoadingSpinner size="small" />
                        Processing...
                    </>
                ) : (
                    children || `Pay €${orderData?.totalAmount?.toFixed(2) || '0.00'}`
                )}
            </Button>

            <div style={{
                textAlign: 'center',
                marginTop: '20px',
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🔒</span> Secure payment powered by Stripe
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                    opacity: 0.7
                }}>
                    <span>Visa</span>
                    <span>Mastercard</span>
                    <span>Amex</span>
                    <span>SEPA</span>
                </div>
            </div>
        </div>
    );
};

export default StripeCheckoutButton;
